/**
 * VideoCallBubble — per-type subcomponent for `ChatMessageType.videoCall`.
 *
 * Flutter source: `currentUserMessageTypeVideoCall` (`chat_screen.dart:1775-1883`)
 * / `peerUserMessageTypeVideoCall` (`:2876-3010`). Variants are structurally
 * identical; differences are row order (own: `[icon][column]`; peer:
 * `[column][icon]`), background color, title/status copy, and the corner-
 * collapse direction handled by `getBubbleCornerRadii`.
 *
 * Non-obvious behaviour:
 *  - `callFailed = !isLive && (audienceStreamingId == null || callDuration
 *    == null)` — Flutter uses OR (`chat_screen.dart:1785-1788` /
 *    `:2886-2889`). Strike-through diagonal renders when `callFailed`.
 *  - Friendly duration uses `useDateFormatters().formatCallDuration` which
 *    ports Flutter's `DateUtils.getFriendlyLiveDuration` with l10n-driven
 *    unit words and two Flutter-bug fixes (typo + dead branch — see hook).
 *  - Peer-tap audience-join (Task 17): a LIVE peer bubble
 *    (`message.isLive === true`) fires `onVideoCallJoin?.(message)` — the page
 *    owns the confirm sheet + permission-gated launch (it has the full
 *    `currentUser` + `groupChatId`). Parity `chat_screen.dart:2899-2924`: a
 *    not-live call is NOT joinable on tap (`:2901`), so the guard short-
 *    circuits. The own-bubble timestamp-toggle branch (`:1798-1813`) is a
 *    separate Flutter path and stays out of scope here.
 *  - Strike-through diagonal: 1.5px × (iconSize + 8) bar rotated -60°,
 *    centered over the cam icon. Lives in the co-located `VideoCallCamIcon`.
 *
 * Responsive / theme mappings are documented inline at each usage site.
 *
 * Deviations from plan:
 *  - The plan body says the strike shows when "callDuration == null &&
 *    audienceStreamingId == null" (AND). Flutter uses OR; followed Flutter
 *    per `conventions.md`.
 *  - Title copy: the plan body is silent on the title line. Flutter renders
 *    title + status; the web l10n stub has `titleOwn` / `titlePeerStarted`
 *    / `titlePeer` keys designed for both lines. Used the stub as designed —
 *    own → `titleOwn` ("You started a video call") deviates from Flutter's
 *    literal "Video call" in favor of the more-explicit web copy.
 */

import React from 'react';
import { ChatMessageType } from '@/domain/enums/chatMessageType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { ChatUtils } from '@/domain/utils/chatUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import {
  registerBubble,
  type BubbleSubcomponentProps,
} from '../../registry/bubbleRegistry';
import { VideoCallCamIcon } from './VideoCallCamIcon';

export const VideoCallBubble: React.FC<BubbleSubcomponentProps> = ({
  message,
  isOwn,
  isLastOfRun,
  onVideoCallJoin,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const { formatCallDuration } = useDateFormatters();
  const chat = l10n.chat;

  const corners = ChatUtils.getBubbleCornerRadii({
    isOwn,
    isLastOfRun,
    runMidRadius: responsive.borderRadius.lg,
    runEndRadius: responsive.borderRadius.sm,
  });

  // `isLive`: a missing field is treated as not-live. Flutter dereferences
  // with `chatMessage.isLive != null && chatMessage.isLive!`
  // (`chat_screen.dart:1782` / `:2883`).
  const isLive = message.isLive === true;

  // `callFailed`: only meaningful in the not-live branch. See header for
  // the Flutter rule (OR between the two null checks).
  const callFailed =
    !isLive &&
    (message.audienceStreamingId === undefined ||
      message.audienceStreamingId === null ||
      message.callDuration === undefined ||
      message.callDuration === null);

  // Title copy. See "Deviations" — own uses the web stub's `titleOwn`
  // (deliberately more explicit than Flutter's literal "Video call");
  // peer-live → `titlePeerStarted`; peer-not-live → `titlePeer`.
  const title = isOwn
    ? chat.videoCall.titleOwn
    : isLive
      ? chat.videoCall.titlePeerStarted
      : chat.videoCall.titlePeer;

  // Status copy. Mirrors Flutter exactly.
  let status: string;
  if (isLive) {
    status = isOwn
      ? chat.videoCall.statusInProgress
      : chat.videoCall.statusTapToJoin;
  } else if (callFailed) {
    status = chat.videoCall.statusFailed;
  } else {
    // Not-live, not-failed → both endpoints have ids and duration, so
    // `callDuration` is guaranteed non-null at this point (see `callFailed`
    // above). The `!` would be safe here; we still guard with `?? 0` so the
    // type narrows without an assertion.
    status = formatCallDuration(message.callDuration ?? 0);
  }

  // Audience-join tap (Task 17). Parity `chat_screen.dart:2900-2905`: only a
  // LIVE call is joinable — a not-live bubble (`isLive` false / absent) returns
  // early and does nothing. On a live bubble we hand off to the page-level
  // `onVideoCallJoin`, which owns the confirm sheet + permission-gated launch
  // (it has the full `currentUser` + `groupChatId` the launch needs). The
  // own-bubble timestamp-toggle branch is a separate Flutter path, out of
  // scope here.
  const handleTap = (): void => {
    if (!isLive) return;
    onVideoCallJoin?.(message);
  };

  const backgroundColor = isOwn ? theme.chat.ownMessage : theme.chat.peerMessage;

  // Cam icon + optional strike-through overlay — extracted to a co-located
  // subcomponent (`VideoCallCamIcon`) to keep this file under the
  // component-size threshold. The strike renders when `failed === true`.
  const iconWithStrike = <VideoCallCamIcon failed={callFailed} />;

  // Title + status column. Same shape on own and peer; only the surrounding
  // row order differs.
  const textColumn = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          color: theme.white,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
        }}
      >
        {title}
      </span>
      <span
        style={{
          color: theme.white,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
          marginTop: responsive.spacing.xxs,
        }}
      >
        {status}
      </span>
    </div>
  );

  return (
    <button
      type="button"
      {...qaAttr({
        id: `chat-bubble-video-call-${message.documentId}`,
        // Reuse the already-computed call state: failed / live / ended.
        status: callFailed ? 'failed' : isLive ? 'live' : 'ended',
      })}
      onClick={handleTap}
      style={{
        backgroundColor,
        border: 'none',
        borderTopLeftRadius: corners.topLeft,
        borderTopRightRadius: corners.topRight,
        borderBottomLeftRadius: corners.bottomLeft,
        borderBottomRightRadius: corners.bottomRight,
        paddingTop: responsive.spacing.sm,
        paddingBottom: responsive.spacing.sm,
        paddingLeft: responsive.spacing.sm,
        paddingRight: responsive.spacing.sm,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        // Icon ↔ column gap → Flutter `Padding(right: 16)` on the icon (own)
        // / `Padding(left: 16)` on the icon (peer). Using flex `gap` on the
        // row produces the identical visual.
        gap: responsive.spacing.lg,
        cursor: 'pointer',
        textAlign: 'left',
        maxWidth: '100%',
      }}
    >
      {/* Row order differs between own and peer: own renders the icon to
          the LEFT of the column (`chat_screen.dart:1836-1875`); peer
          renders the column to the LEFT of the icon
          (`chat_screen.dart:2960-3002`). */}
      {isOwn ? (
        <>
          {iconWithStrike}
          {textColumn}
        </>
      ) : (
        <>
          {textColumn}
          {iconWithStrike}
        </>
      )}
    </button>
  );
};

// Register with the dispatcher so `MessageBubble` routes
// `ChatMessageType.videoCall` to this component. See `MessageBubble.tsx`
// for the registry contract.
registerBubble(ChatMessageType.videoCall, VideoCallBubble);
